import { forwardRef, useEffect, useImperativeHandle, useState, type CSSProperties } from "react";
import { ReactRenderer } from "@tiptap/react";
import Mention from "@tiptap/extension-mention";
import type { SuggestionOptions } from "@tiptap/suggestion";
import type { MentionItem } from "@/lib/types";
import { compareMentionByName } from "@/lib/boardPeople";
import { mentionPopupPlacement } from "@/lib/mentionPopupPlacement";

interface MentionListRef {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

const listStyle: CSSProperties = {
  minWidth: 200,
  maxWidth: 280,
  maxHeight: 260,
  overflowY: "auto",
  background: "var(--surface-elev)",
  border: "1px solid var(--hairline-strong)",
  borderRadius: 10,
  boxShadow: "0 10px 28px rgba(13,62,99,0.18), 0 2px 6px rgba(13,62,99,0.06)",
  padding: 5,
};

const MentionList = forwardRef<
  MentionListRef,
  { items: MentionItem[]; command: (item: MentionItem) => void }
>(function MentionList(props, ref) {
  const [selected, setSelected] = useState(0);
  useEffect(() => setSelected(0), [props.items]);

  const pick = (index: number) => {
    const item = props.items[index];
    if (item) props.command({ id: item.id, label: item.label });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: (event) => {
      const n = props.items.length;
      if (n === 0) return false;
      if (event.key === "ArrowDown") {
        setSelected((s) => (s + 1) % n);
        return true;
      }
      if (event.key === "ArrowUp") {
        setSelected((s) => (s - 1 + n) % n);
        return true;
      }
      if (event.key === "Enter") {
        pick(selected);
        return true;
      }
      return false;
    },
  }));

  if (props.items.length === 0) {
    return (
      <div style={{ ...listStyle, padding: "10px 12px", fontSize: 13, color: "var(--ink-4)" }}>
        Keine Treffer
      </div>
    );
  }
  return (
    <div style={listStyle}>
      {props.items.map((item, i) => (
        <button
          key={item.id}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setSelected(i)}
          onClick={() => pick(i)}
          style={{
            display: "block",
            width: "100%",
            textAlign: "left",
            padding: "7px 9px",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            color: "var(--ink-1)",
            background: i === selected ? "var(--surface-soft)" : "transparent",
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
});

// Mention node that serializes to `[label](mention:<id>)` markdown so the body
// stays plain markdown (backend parses the id; MarkdownContent renders a chip).
export const MarkdownMention = Mention.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(
          state: { write: (s: string) => void },
          node: { attrs: { id: string; label?: string | null } },
        ) {
          const id = node.attrs.id;
          const label = node.attrs.label ?? id;
          state.write(`[${label}](mention:${id})`);
        },
        parse: {
          // Reloading a stored entry: tiptap-markdown renders our serialized
          // `[label](mention:<id>)` as a plain `<a href="mention:id">`, which
          // TipTap's Link mark then DROPS — the `mention:` scheme isn't in
          // Link.isAllowedUri's allowlist, so the anchor collapses to bare text
          // and the leading @mention is lost. On the next save the backend sees
          // no leading mention and UNLINKS the to-do (it reverts to a note).
          // Rewrite those anchors back into mention spans (which the Mention
          // node's parseHTML claims) BEFORE TipTap parses the HTML, so a
          // reopened entry keeps its @mention — and stays a to-do.
          updateDOM(element: HTMLElement) {
            element.querySelectorAll('a[href^="mention:"]').forEach((a) => {
              const id = (a.getAttribute("href") || "").slice("mention:".length);
              if (!id) return;
              const label = a.textContent || id;
              const span = a.ownerDocument.createElement("span");
              span.setAttribute("data-type", "mention");
              span.setAttribute("data-id", id);
              span.setAttribute("data-label", label);
              span.textContent = `@${label}`;
              a.replaceWith(span);
            });
          },
        },
      },
    };
  },
});

function placePopup(el: HTMLDivElement, rect: DOMRect | null | undefined) {
  if (!rect) return;
  // Flip above the caret when the list doesn't fit below (a composer low in the
  // viewport otherwise clips the last names). offsetHeight is the list's real height
  // (<= its 260px max); onUpdate re-runs this per keystroke so it self-corrects.
  const { top } = mentionPopupPlacement({
    caretTop: rect.top,
    caretBottom: rect.bottom,
    popupHeight: el.offsetHeight,
    viewportHeight: window.innerHeight,
  });
  el.style.top = `${top}px`;
  el.style.left = `${rect.left}px`;
}

export function makeMentionSuggestion(
  getItems: () => MentionItem[],
  onOpenChange?: (open: boolean) => void,
): Omit<SuggestionOptions<MentionItem>, "editor"> {
  return {
    char: "@",
    items: ({ query }) => {
      const q = query.toLowerCase();
      // Alphabetical by display name (first name first — user preference), so the 8
      // shown are the alphabetically-first matches, not insertion order. filter() copies.
      return getItems()
        .filter((i) => i.label.toLowerCase().includes(q))
        .sort(compareMentionByName)
        .slice(0, 8);
    },
    render: () => {
      let renderer: ReactRenderer<MentionListRef> | null = null;
      let popup: HTMLDivElement | null = null;
      return {
        onStart: (props) => {
          onOpenChange?.(true);
          renderer = new ReactRenderer(MentionList, { props, editor: props.editor });
          popup = document.createElement("div");
          popup.style.position = "fixed";
          popup.style.zIndex = "200";
          document.body.appendChild(popup);
          popup.appendChild(renderer.element);
          placePopup(popup, props.clientRect?.());
        },
        onUpdate: (props) => {
          renderer?.updateProps(props);
          if (popup) placePopup(popup, props.clientRect?.());
        },
        onKeyDown: (props) => {
          if (props.event.key === "Escape") return true;
          return renderer?.ref?.onKeyDown(props.event) ?? false;
        },
        onExit: () => {
          onOpenChange?.(false);
          popup?.remove();
          renderer?.destroy();
          popup = null;
          renderer = null;
        },
      };
    },
  };
}
